import { computed, watch, type WritableComputedRef } from 'vue'
import { useRoute, useRouter, type LocationQuery, type Router } from 'vue-router'
import { useRangeStore, type RangeState } from '@/stores/range'
import { useVariablesStore } from '@/stores/variables'
import type { VariableValues } from '@/types'

const RANGE_SUFFIX: Record<RangeState['unit'], string> = { minute: 'm', hour: 'h', day: 'd', week: 'w' }
const VARIABLE_PREFIX = 'var.'

let patch: Record<string, string | string[] | undefined> | null = null

export const putQuery = (router: Router, next: Record<string, string | string[] | undefined>) => {
  const queued = patch !== null
  patch = { ...(patch ?? {}), ...next }
  if (queued) return
  queueMicrotask(() => {
    const route = router.currentRoute.value
    const query: LocationQuery = { ...route.query }
    for (const [key, value] of Object.entries(patch ?? {})) {
      if (value === undefined || value === '' || (Array.isArray(value) && !value.length)) delete query[key]
      else query[key] = value
    }
    patch = null
    void router.replace({ query, hash: route.hash })
  })
}

export const useQueryText = <T extends string = string>(key: string, initial: NoInfer<T>): WritableComputedRef<T> => {
  const route = useRoute()
  const router = useRouter()
  return computed<T>({
    get: () => (typeof route.query[key] === 'string' ? (route.query[key] as T) : initial),
    set: (value) => putQuery(router, { [key]: value === initial ? undefined : value }),
  })
}

export const useQueryFlag = (key: string, initial = false): WritableComputedRef<boolean> => {
  const route = useRoute()
  const router = useRouter()
  return computed<boolean>({
    get: () => (typeof route.query[key] === 'string' ? route.query[key] === '1' : initial),
    set: (value) => putQuery(router, { [key]: value === initial ? undefined : value ? '1' : '0' }),
  })
}

export const getRangeParams = (state: RangeState): { from?: string; to?: string } => {
  if (state.mode === 'relative') return { from: `${state.count}${RANGE_SUFFIX[state.unit]}` }
  if (state.mode === 'absolute' && state.from && state.to) return { from: state.from, to: state.to }
  return {}
}

export const useRangeQuery = () => {
  const route = useRoute()
  const router = useRouter()
  const rangeStore = useRangeStore()
  const read = () => {
    const { from, to } = route.query
    if (typeof from === 'string') rangeStore.setRangeQuery(from, typeof to === 'string' ? to : undefined)
    else if (rangeStore.state.mode !== 'live') rangeStore.setRangeLive()
  }
  read()
  watch(() => [route.query.from, route.query.to], ([from, to]) => {
    const held = getRangeParams(rangeStore.state)
    if (held.from !== from || held.to !== to) read()
  })
  watch(() => getRangeParams(rangeStore.state), (next) => putQuery(router, { from: next.from, to: next.to }), { deep: true })
}

export const getVariableQuery = (query: LocationQuery): VariableValues =>
  Object.fromEntries(
    Object.entries(query)
      .filter(([key]) => key.startsWith(VARIABLE_PREFIX))
      .map(([key, value]) => [key.slice(VARIABLE_PREFIX.length), (Array.isArray(value) ? value : [value]).filter((v): v is string => typeof v === 'string')]),
  )

export const useVariableQuery = (pageId: () => string | null) => {
  const route = useRoute()
  const router = useRouter()
  const variablesStore = useVariablesStore()
  watch(pageId, (id) => {
    if (!id) return
    const values = getVariableQuery(route.query)
    for (const [name, picked] of Object.entries(values)) variablesStore.putVariable(id, name, picked)
  }, { immediate: true })
  watch(() => {
    const id = pageId()
    return id ? variablesStore.getVariableValues(id) : {}
  }, (values, before) => {
    const next: Record<string, string[] | undefined> = {}
    for (const key of Object.keys(before ?? {})) next[`${VARIABLE_PREFIX}${key}`] = undefined
    for (const [key, value] of Object.entries(values)) next[`${VARIABLE_PREFIX}${key}`] = value
    putQuery(router, next)
  }, { deep: true })
}
